import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Panel from '../panel'
import TeamInfo from './team-info'
import TeamMembers from './team-members'
import FlashAlert from '../../../../components/flash-alert'
import { ajaxGet, ajaxDelete, patchForm } from '../../../../ajax'

export default function EditTeam({id, league, balance, formRef, onClose, onUpdate, onDelete, onChanged}) {
  const { register, control, reset, handleSubmit, setValue, getValues } = useForm({})

  const [team, setTeam] = useState()
  const [members, setMembers] = useState([])
  const [initialMembers, setInitialMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentTab, setCurrentTab] = useState('Team Info')
  const [alert, setAlert] = useState(null)
  const [changes, setChanges] = useState({})

  const isInfo = currentTab == 'Team Info'
  const isPlayers = currentTab == 'Players'

  useEffect(() => {
    setCurrentTab('Team Info')
    loadTeam()
  }, [id])

  useEffect(() => {
    if (team) reset({...team, color: teamColor(team.color)})
  }, [team])

  useEffect(() => {
    onChanged(Object.values(changes).some(x => x))
  }, [changes])

  const teamColor = (color) => {
    if (!team.color) return ''

    return team.color.split(', ').map(color => {
      const isDark = ['Yellow', 'White'].includes(color)
      return { value: color, label: color, className: `color-value ${color.toLowerCase()}${isDark ? ' dark-color' : ''}` }
    })
  }

  const changeValue = (name, value) => {
    setValue(name, value)
    setChanges({...changes, [name]: value != team[name] })
  }

  const membersChanged = (value) => {
    if (initialMembers.length != value.length) return true

    const initialIds = initialMembers.map(m => m.player_id).sort()
    const newIds = value.map(m => m.player_id).sort()
    if (!_.isEqual(initialIds, newIds)) return true

    for (let i = 0; i < initialMembers.length; i++) {
      const initialValue = initialMembers[i]
      const newMember = value.find(m => m.player_id == initialValue.player_id)
      if (!_.isEqual(initialValue, newMember)) return true
    }
    return false
  }

  const changeMembers = (value) => {
    setMembers(value)
    setChanges({...changes, members: membersChanged(value) })
  }

  const loadTeam = () => {
    setLoading(true)

    ajaxGet(`/admins/leagues/${league.id}/teams/${id}/edit.json`)
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      setTeam(json.team)
      setMembers(json.members)
      setInitialMembers(json.members)
      setLoading(false)
    })
  }

  const deleteTeam = () => {
    setLoading(true)

    ajaxDelete(`/admins/leagues/${league.id}/teams/${id}.json`)
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      setLoading(false)
      onDelete(id)
    })
  }
  
  const prepareMembersData = (formData) => {
    members.forEach((member, i) => {
      if (member.id) formData.append(`team[members_attributes][${i}][id]`, member.id)
      formData.append(`team[members_attributes][${i}][player_id]`, member.player_id)
      formData.append(`team[members_attributes][${i}][league_id]`, league.id)
      formData.append(`team[members_attributes][${i}][captain]`, member.captain)
      if (member._destroy) formData.append(`team[members_attributes][${i}][_destroy]`, true)
    })
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('team[name]', data.name.trim())
    formData.append('team[notes]', data.notes.trim())
    if (data.photo_url) formData.append('team[photo_url]', data.photo_url)
    formData.append('team[color]', data.color ? data.color.map(v => v.label).join(', ') : '')
    formData.append('team[about]', data.about)
    prepareMembersData(formData)
    return formData
  }

  const clickCancel = (e) => {
    e.preventDefault()
    onClose()
  }

  const onSubmit = (data, event) => {
    if (event.target.className != 'edit-team-form') return

    setLoading(true)

    let formData = prepareFormData(data)

    patchForm(`/admins/leagues/${league.id}/teams/${id}.json`, formData)
    .then(response => { return response.json() })
    .then((json) => {
      setLoading(false)
      if (json.alert)
        setAlert(json.alert)
      else {
        onUpdate(json.team)
      }
    })
  }

  return <form className={'edit-team-form' + (loading ? ' isLoading' : '')} ref={formRef} onSubmit={handleSubmit(onSubmit)}>
    { loading && <div className='animated-spinner'></div> }
    <Panel tabs={['Team Info', 'Players']} currentTab={currentTab} changeTab={(value) => setCurrentTab(value)} />
    { alert && <FlashAlert text={alert.text} type={alert.type} onHide={() => setAlert(null)} />}
    { team && isInfo && 
      <TeamInfo
        initialName={team.name}
        balance={balance}
        league={league}
        register={register}
        control={control}
        setValue={changeValue}
        getValues={getValues}
        onDelete={deleteTeam}
      />
    }
    { team && isPlayers && 
      <TeamMembers
        league={league}
        team={team}
        members={members}
        setMembers={changeMembers}
        onAlert={message => setAlert({ text: message, type: 'alert' })}
      />
    }
    <div className='team-info-buttons'>
      <button className='team-button button-cancel' onClick={clickCancel}>Cancel</button>
      <button className='team-button button-save' type='submit'>Save changes</button>
    </div>
  </form>
}
